import React, { useState } from 'react';
import { Button, Modal, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUndo,
    faInfoCircle,
    faMoneyBillWave,
    faUnlock
} from '@fortawesome/free-solid-svg-icons';
import { FormaPagamentoType } from 'services/formaPagamentoService';
import { useReativarFormaPagamento } from 'hooks/useFormaPagamento';
import { useToast } from 'providers/ToastProvider';

interface BotaoReativarFormaPagamentoProps {
    formaPagamento: FormaPagamentoType;
    size?: "sm" | "lg";
    variant?: string;
    title?: string;
}

const BotaoReativarFormaPagamento: React.FC<BotaoReativarFormaPagamentoProps> = ({
    formaPagamento,
    size = "sm",
    variant = "outline-success",
    title = "Reativar forma de pagamento"
}) => {
    const [showModal, setShowModal] = useState(false);

    // Hooks
    const reativarMutation = useReativarFormaPagamento();
    const { showToast } = useToast();

    const handleClick = () => setShowModal(true);
    const handleClose = () => {
        setShowModal(false);
        reativarMutation.reset();
    };

    const handleConfirm = async () => {
        try {
            await reativarMutation.mutateAsync(formaPagamento.id);
            showToast('Forma de pagamento reativada com sucesso', 'success');
            handleClose();
        } catch (error) {
            console.error('Erro ao reativar forma de pagamento:', error);
        }
    };

    return (
        <>
            <Button
                variant={variant}
                size={size}
                onClick={handleClick}
                title={title}
            >
                <FontAwesomeIcon icon={faUndo} />
            </Button>

            <Modal show={showModal} onHide={handleClose} centered size="lg" className="fade">
                <Modal.Header className="border-0 pb-0">
                    <div className="d-flex justify-content-center w-100">
                        <div className="rounded-circle bg-success bg-opacity-10 p-3 mb-3">
                            <FontAwesomeIcon
                                icon={faUnlock}
                                className="text-success fa-2x"
                            />
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="text-center px-4 pt-0">
                    {reativarMutation.isError && (
                        <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle mb-3">
                            <div className="text-danger-emphasis">
                                <strong>Erro:</strong> Não foi possível reativar a forma de pagamento.
                                Tente novamente.
                            </div>
                        </Alert>
                    )}

                    <h4 className="mb-3">Restaurar Forma de Pagamento</h4>
                    <p className="mb-3">Você está prestes a restaurar a forma de pagamento:</p>

                    <div className="d-flex align-items-center justify-content-center mb-4">
                        <div className="border rounded-4 py-3 px-4 bg-body">
                            <div className="d-flex align-items-center">
                                <div className="rounded-circle bg-success bg-opacity-10 p-2 me-3 shadow-sm">
                                    <FontAwesomeIcon icon={faMoneyBillWave} className="text-success fa-2x" />
                                </div>
                                <div className="text-start">
                                    <h5 className="mb-0">{formaPagamento.descricao}</h5>
                                    <div className="d-flex align-items-center text-muted">
                                        <small className="me-2">Código:</small>
                                        <span className="badge bg-secondary">
                                            #{formaPagamento.codigo}
                                        </span>
                                        <small className="ms-3 me-2">Tipo:</small>
                                        <span className="badge bg-light text-body-emphasis border">
                                            {formaPagamento.tipo}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-body border-start border-4 border-info rounded p-3 mb-3 text-start">
                        <h6 className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-info" />
                            Informações Importantes
                        </h6>
                        <p className="text-muted small mb-0">
                            Ao restaurar esta forma de pagamento, ela voltará a ter acesso ao sistema conforme suas configurações.
                            Ela aparecerá novamente nas listagens e poderá ser utilizada normalmente.
                        </p>
                    </div>
                </Modal.Body>

                <Modal.Footer className="border-0 justify-content-center pt-0">
                    <Button
                        variant="outline-secondary"
                        onClick={handleClose}
                        disabled={reativarMutation.isPending}
                        className="px-4"
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleConfirm}
                        disabled={reativarMutation.isPending}
                        className="px-4"
                    >
                        {reativarMutation.isPending ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Restaurando...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faUnlock} className="me-2" />
                                Restaurar Forma de Pagamento
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BotaoReativarFormaPagamento; 